import {
  AuthFlow,
  codeLine,
  resendLabel,
  resendWait,
  type AuthClient,
  type FlowState,
} from '../../../shared/auth/flow';
import {
  providerLabel,
  type AuthMode,
  type Provider,
} from '../../../shared/auth/providers';
import { EMAIL_ERROR, EMAIL_PATTERN } from '../onboarding';
import { $, $$, attr, text } from './dom';
import { reducedMotion } from './motion';

export interface SignupOptions {
  /** Clerk, as the sign-in page's steps ask for it (client/clerk.ts). */
  client: AuthClient;
  /** Where an account goes once in, with the rule as it now stands. */
  destination: () => string;
  go: (href: string) => void;
  /** The real clock, for the resend lock. */
  now?: () => number;
}

/** The submit button, by form: the brief's, and the sign-in page's. */
const SUBMIT: Record<AuthMode, string> = {
  signup: 'Create free account',
  signin: 'Continue',
};

/** Screen C's heading on the sign-in page (web/src/auth/copy.ts). */
const DONE: Record<AuthMode, string> = {
  signup: 'Account created',
  signin: 'You’re in',
};

/** A click the browser should keep: a new tab, a new window, a download. */
function modified(e: MouseEvent): boolean {
  return e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
}

/** The sign-in page's refusal shake (web/src/auth/hooks.ts). */
function shake(el: Element | null): void {
  if (!el || reducedMotion() || typeof el.animate !== 'function') return;
  el.animate(
    [
      { transform: 'translateX(0)' },
      { transform: 'translateX(-6px)' },
      { transform: 'translateX(6px)' },
      { transform: 'translateX(-4px)' },
      { transform: 'translateX(4px)' },
      { transform: 'translateX(0)' },
    ],
    { duration: 400, easing: 'ease-in-out' },
  );
}

/**
 * /get-started's step 4: signing up, or in, without leaving the page. The
 * sign-in page's own steps (AuthFlow) drive it, on the page's Clerk: Google
 * or Apple, or an email address, then the 6-digit code Clerk emails, then on
 * to the dashboard with the rule. An address that has an account is sent a
 * sign-in code all the same (AuthFlow.submitEmail). The words for each step
 * are the sign-in page's (web/src/auth/AuthPage.tsx), bar the brief's own:
 * the submit button and the empty-address line.
 *
 * Signed in already (`data-signed-in`), every way in goes straight on.
 */
export function signupPanel(root: HTMLElement, options: SignupOptions): void {
  const panel = $(root, '[data-auth]');
  const start = $(root, '[data-auth-start]');
  const codeScreen = $(root, '[data-auth-code]');
  const done = $(root, '[data-auth-done]');
  const form = $<HTMLFormElement>(root, '[data-signup]');
  const email = $<HTMLInputElement>(root, '[data-email]');
  const emailError = $(root, '[data-email-err]');
  const submit = $<HTMLButtonElement>(root, '[data-submit]');
  const codeInput = $<HTMLInputElement>(root, '[data-code-input]');
  const boxes = $(root, '[data-code-boxes]');
  if (!panel || !start || !codeScreen || !done || !form || !email) return;
  if (!emailError || !submit || !codeInput || !boxes) return;

  const now = options.now ?? Date.now;
  const signedIn = $(root, '[data-providers]')?.hasAttribute('data-signed-in');
  const links = $$<HTMLAnchorElement>(root, 'a[data-provider]');
  const oauthError = $(root, '[data-oauth-error]');
  const toSignInLine = $(root, '[data-to-signin-line]');
  const resend = $<HTMLButtonElement>(root, '[data-resend]');
  const status = $(root, '[data-code-status]');

  const flow = new AuthFlow(options.client, { mode: 'signup', now });
  // The brief's own line for an empty or unusable address, before Clerk.
  let ownError: string | null = null;
  let codeFocused = false;
  let left = false;
  let resendTimer: number | undefined;
  let last: FlowState = flow.getState();

  const leave = () => {
    if (left) return;
    left = true;
    options.go(options.destination());
  };

  const paintStart = (s: FlowState) => {
    const busy = s.pending !== null;
    for (const link of links) {
      const provider = link.dataset.provider as Provider;
      const going = s.pending === provider;
      text($(link, '[data-provider-label]'), providerLabel(provider, going));
      if (going) attr(link, 'aria-busy', 'true');
      else link.removeAttribute('aria-busy');
      if (busy && !going) attr(link, 'aria-disabled', 'true');
      else link.removeAttribute('aria-disabled');
    }
    text(oauthError, s.oauthError ?? '');

    submit.disabled = busy;
    if (s.pending === 'email') attr(submit, 'aria-busy', 'true');
    else submit.removeAttribute('aria-busy');
    text(submit, SUBMIT[s.mode]);
    // The sign-in form, as on the sign-in page, has no line back to it.
    if (toSignInLine) toSignInLine.hidden = s.mode === 'signin';

    const issue = s.emailIssue;
    text(emailError, ownError ?? issue?.message ?? '');
    if (ownError || issue) attr(email, 'aria-invalid', 'true');
    else email.removeAttribute('aria-invalid');
  };

  const paintCode = (s: FlowState) => {
    text($(codeScreen, '[data-code-email]'), s.email);
    const checking = s.codeStatus === 'checking';
    const refused = s.codeStatus === 'wrong' || s.codeStatus === 'expired';
    boxes.classList.toggle('is-checking', checking);
    boxes.classList.toggle('is-refused', refused);
    const next = codeFocused && !checking ? s.code.length : -1;
    $$(boxes, '.onb-box').forEach((box, i) => {
      text(box, s.code[i] ?? '');
      box.classList.toggle('is-filled', Boolean(s.code[i]));
      box.classList.toggle('is-next', i === next);
    });
    if (codeInput.value !== s.code) codeInput.value = s.code;
    codeInput.readOnly = checking;
    if (refused) attr(codeInput, 'aria-invalid', 'true');
    else codeInput.removeAttribute('aria-invalid');

    if (status) {
      status.classList.toggle('is-error', refused || s.codeStatus === 'failed');
      status.replaceChildren();
      if (checking) {
        const spinner = document.createElement('span');
        spinner.className = 'onb-spinner';
        attr(spinner, 'aria-hidden', 'true');
        status.append(spinner);
      }
      status.append(codeLine(s));
    }

    // "Resend code in 0:29", a tick a second while it is locked.
    const wait = resendWait(now(), s.resendAt);
    if (resend) {
      text(resend, resendLabel(wait));
      resend.disabled = wait > 0 || s.resending || checking;
    }
    window.clearTimeout(resendTimer);
    if (wait > 0) {
      resendTimer = window.setTimeout(() => paint(flow.getState()), 1000);
    }
  };

  const paint = (s: FlowState) => {
    start.hidden = s.screen !== 'start';
    codeScreen.hidden = s.screen !== 'code';
    done.hidden = s.screen !== 'done';
    if (s.screen === 'start') paintStart(s);
    if (s.screen === 'code') paintCode(s);
    if (s.screen === 'done') {
      text($(done, '[data-done-h]'), DONE[s.doneAs ?? s.mode]);
    }
  };

  // What a step changed beyond the drawing: focus, a shake, leaving.
  flow.subscribe(() => {
    const s = flow.getState();
    paint(s);
    if (s.screen !== last.screen) {
      if (s.screen === 'code') codeInput.focus();
      if (s.screen === 'start') email.focus();
      if (s.screen === 'done') {
        $<HTMLElement>(done, '[data-done-h]')?.focus();
        leave();
      }
    }
    if (s.emailShake > last.emailShake) {
      shake(email);
      email.focus();
    }
    if (s.codeShake > last.codeShake) {
      shake(boxes);
      codeInput.focus();
    }
    last = s;
  });

  for (const link of links) {
    link.addEventListener('click', (e) => {
      if (modified(e)) return;
      e.preventDefault();
      if (signedIn) {
        leave();
        return;
      }
      void flow.oauth(link.dataset.provider as Provider);
    });
  }
  // Back from the provider's page by the back button: the buttons work again.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) flow.restore();
  });

  email.addEventListener('input', () => {
    ownError = null;
    flow.setEmail(email.value);
    paint(flow.getState());
  });
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (signedIn) {
      leave();
      return;
    }
    flow.setEmail(email.value);
    if (!EMAIL_PATTERN.test(email.value.trim())) {
      ownError = EMAIL_ERROR;
      paint(flow.getState());
      shake(email);
      email.focus();
      return;
    }
    void flow.submitEmail();
  });
  $(root, '[data-to-signin]')?.addEventListener('click', (e) => {
    if (modified(e)) return;
    e.preventDefault();
    flow.swap('signin');
    email.focus();
  });

  codeInput.addEventListener('input', () => flow.setCode(codeInput.value));
  // maxlength would cut "123 456" to "123 45" before the digits are picked
  // out, so a paste is read whole.
  codeInput.addEventListener('paste', (e) => {
    e.preventDefault();
    flow.setCode(e.clipboardData?.getData('text') ?? '');
  });
  codeInput.addEventListener('focus', () => {
    codeFocused = true;
    paint(flow.getState());
  });
  codeInput.addEventListener('blur', () => {
    codeFocused = false;
    paint(flow.getState());
  });
  $(root, '[data-code-back]')?.addEventListener('click', () => flow.back());
  resend?.addEventListener('click', () => void flow.resend());

  paint(flow.getState());
}
